import { useState, useEffect, useRef } from 'react'
import { FileText, Download, Users, Target } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { pisteirosService } from '../../services/pisteirosService'
import api from '../../services/api'
import toast from 'react-hot-toast'

export default function RelatorioCarteiraPisteiros() {
  const { organizadorAtivo } = useOrganizador()
  const navigate = useNavigate()
  const orgId = organizadorAtivo?.id
  const moduloAtivo = organizadorAtivo?.produtos_premium?.includes('quem_vendeu_trouxe')
  const iframeRef = useRef(null)

  const [pisteiros, setPisteiros] = useState([])
  const [filtros, setFiltros] = useState({ pisteiro_id: '' })
  const [loading, setLoading] = useState(false)
  const [dados, setDados] = useState(null)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdfModal, setShowPdfModal] = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(false)

  useEffect(() => {
    if (!moduloAtivo) {
      navigate('/dashboard', { replace: true })
    }
  }, [moduloAtivo, navigate])

  useEffect(() => {
    if (!orgId) return
    pisteirosService.listar(orgId)
      .then(res => setPisteiros(res.data || res || []))
      .catch(() => {})
  }, [orgId])

  const carregar = async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filtros.pisteiro_id === 'sem_pisteiro') {
        params.append('sem_pisteiro', 'true')
      } else if (filtros.pisteiro_id) {
        params.append('pisteiro_id', filtros.pisteiro_id)
      }
      const query = params.toString()
      const res = await api.get(`/organizadores/${orgId}/carteira-pisteiros${query ? `?${query}` : ''}`)
      setDados(res.data)
    } catch (err) {
      toast.error('Erro ao carregar carteira de pisteiros')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { carregar() }, [orgId])

  const gerarPdf = async () => {
    if (!orgId) return
    setLoadingPdf(true)
    try {
      const params = new URLSearchParams()
      if (filtros.pisteiro_id === 'sem_pisteiro') {
        params.append('sem_pisteiro', 'true')
      } else if (filtros.pisteiro_id) {
        params.append('pisteiro_id', filtros.pisteiro_id)
      }
      const query = params.toString()
      const res = await api.get(
        `/organizadores/${orgId}/carteira-pisteiros/pdf${query ? `?${query}` : ''}`,
        { responseType: 'blob' }
      )
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      setPdfUrl(url)
      setShowPdfModal(true)
    } catch (err) {
      toast.error('Erro ao gerar PDF')
    } finally {
      setLoadingPdf(false)
    }
  }

  const printPdf = () => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.print()
    }
  }

  if (!moduloAtivo) return null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-xl">
            <Target className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Carteira de Pisteiros</h1>
            <p className="text-purple-200 text-sm">Clientes vinculados a cada pisteiro</p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Pisteiro</label>
            <select
              value={filtros.pisteiro_id}
              onChange={(e) => setFiltros({ ...filtros, pisteiro_id: e.target.value })}
              className="input-rematei"
            >
              <option value="">Todos os pisteiros</option>
              <option value="sem_pisteiro">— Sem pisteiro —</option>
              {pisteiros.map(p => (
                <option key={p.id} value={p.id}>{p.nome}</option>
              ))}
            </select>
          </div>
          <button
            onClick={carregar}
            disabled={loading}
            className="px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? 'Carregando...' : 'Filtrar'}
          </button>
          <button
            onClick={gerarPdf}
            disabled={loadingPdf}
            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-900 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <FileText className="w-4 h-4" />
            {loadingPdf ? 'Gerando...' : 'Gerar PDF'}
          </button>
        </div>
      </div>

      {/* Resultado */}
      {dados && (
        <div className="space-y-4">
          {dados.carteiras?.length === 0 && !dados.clientes_sem_pisteiro?.length ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">Nenhum pisteiro com clientes vinculados</p>
            </div>
          ) : (
            dados.carteiras?.map((item) => (
              <div key={item.pisteiro.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Header do pisteiro */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 px-5 py-3 border-b border-purple-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 bg-purple-100 rounded-lg">
                      <Target className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{item.pisteiro.nome}</h3>
                      {item.pisteiro.telefone && (
                        <p className="text-xs text-gray-500">{item.pisteiro.telefone}</p>
                      )}
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-semibold rounded-full">
                    {item.total_clientes} cliente{item.total_clientes !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Tabela de clientes */}
                {item.clientes.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">CPF/CNPJ</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fazenda</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cidade/UF</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Telefone</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {item.clientes.map((c, idx) => (
                          <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-2 text-gray-400">{idx + 1}</td>
                            <td className="px-4 py-2 font-medium text-gray-900">{c.nome}</td>
                            <td className="px-4 py-2 text-gray-600">{c.cpf_cnpj || '-'}</td>
                            <td className="px-4 py-2 text-gray-600">{c.fazenda || '-'}</td>
                            <td className="px-4 py-2 text-gray-600">
                              {c.cidade ? `${c.cidade}/${c.estado || ''}` : '-'}
                            </td>
                            <td className="px-4 py-2 text-gray-600">{c.telefone || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))
          )}

          {dados.clientes_sem_pisteiro?.length > 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-100 to-gray-50 px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 bg-gray-200 rounded-lg">
                    <Users className="w-4 h-4 text-gray-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Clientes sem pisteiro vinculado</h3>
                </div>
                <span className="px-3 py-1 bg-gray-200 text-gray-700 text-sm font-semibold rounded-full">
                  {dados.clientes_sem_pisteiro.length} cliente{dados.clientes_sem_pisteiro.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">CPF/CNPJ</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fazenda</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cidade/UF</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Telefone</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {dados.clientes_sem_pisteiro.map((c, idx) => (
                      <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-2 text-gray-400">{idx + 1}</td>
                        <td className="px-4 py-2 font-medium text-gray-900">{c.nome}</td>
                        <td className="px-4 py-2 text-gray-600">{c.cpf_cnpj || '-'}</td>
                        <td className="px-4 py-2 text-gray-600">{c.fazenda || '-'}</td>
                        <td className="px-4 py-2 text-gray-600">
                          {c.cidade ? `${c.cidade}/${c.estado || ''}` : '-'}
                        </td>
                        <td className="px-4 py-2 text-gray-600">{c.telefone || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            dados.total_sem_pisteiro > 0 && (
              <div className="bg-gray-50 rounded-xl border border-gray-200 px-5 py-3 flex items-center justify-between">
                <span className="text-sm text-gray-600">Clientes sem pisteiro vinculado</span>
                <span className="px-3 py-1 bg-gray-200 text-gray-700 text-sm font-semibold rounded-full">
                  {dados.total_sem_pisteiro}
                </span>
              </div>
            )
          )}
        </div>
      )}

      {/* Modal PDF */}
      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setShowPdfModal(false); URL.revokeObjectURL(pdfUrl) }} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800">Carteira de Pisteiros</h3>
              <div className="flex items-center gap-2">
                <button onClick={printPdf} className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                  Imprimir
                </button>
                <button
                  onClick={() => { setShowPdfModal(false); URL.revokeObjectURL(pdfUrl) }}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Fechar
                </button>
              </div>
            </div>
            <div className="flex-1 p-2">
              <iframe ref={iframeRef} src={pdfUrl} className="w-full h-full rounded border" title="PDF Carteira Pisteiros" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
